import { LAYER } from '../layers';
import { Check, ChevronDown, Database, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { HelpTip } from '../HelpTip';
import { createPortal } from 'react-dom';
import { useFloatingPanel } from '../../hooks/useFloatingPanel';
import { sortOptions, type SelectOption, type SelectSort } from './selectShared';
import { useDeferredError } from './useDeferredError';

/**
 * Search-first multi-select — reference §14. Picks render as removable chips.
 *
 * An option says WHAT it is, as `<Select>`'s does: the `sublabel` line renders under the label,
 * and the filter matches `keywords` as well as the label — a second line nobody can search for
 * is text the reader types and finds nothing. It silently dropped both until 08 Oct 2026, so a
 * list of bare codes reached the screen while its caller had passed the description.
 *
 * Differs from `<Select>` deliberately: the panel stays open while toggling, and there is no
 * `seamless` or `allowCustom` — a multi-select in a table cell and free-text multi-entry are
 * both different problems with different components.
 */
export function MultiSelect({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  disabled = false,
  loading = false,
  error,
  sort = 'asc',
  searchable = true,
  remote = false,
  closeOnSelect = false,
  id,
  name,
  ariaLabel,
  className = '',
  label,
  description,
  required = false,
}: {
  value: string[];
  onChange: (value: string[]) => void;
  /** Only `value`, `label`, and `disabled` are used. */
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  error?: string;
  sort?: SelectSort;
  searchable?: boolean;
  remote?: boolean;
  closeOnSelect?: boolean;
  id: string;
  name?: string;
  ariaLabel?: string;
  className?: string;
  label?: string;
  description?: string;
  required?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const { triggerRef, panelRef, rect } = useFloatingPanel(open, () => setOpen(false));

  const sorted = useMemo(() => sortOptions(options, sort), [options, sort]);

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    return term && searchable
      ? sorted.filter(
          (o) => o.label.toLowerCase().includes(term) || (o.keywords?.toLowerCase().includes(term) ?? false),
        )
      : sorted;
  }, [sorted, query, searchable]);

  const chosen = sorted.filter((o) => value.includes(o.value));

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  function toggle(optionValue: string) {
    onChange(
      value.includes(optionValue)
        ? value.filter((v) => v !== optionValue)
        : [...value, optionValue],
    );

    if (closeOnSelect) {
      setOpen(false);
    }
  }

  /*
    A required-but-empty field must not open red. `useDeferredError` holds back an error the
    control has carried since it mounted until the reader has touched it, and shows anything
    that arrives later — a server rejection included — immediately.
  */
  const { error: shownError, markTouched } = useDeferredError(error);

  const describedBy = [description ? `${id}-desc` : null, shownError ? `${id}-err` : null]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="flex flex-col gap-1.5">
      {label ? (
        <div className="flex min-h-5 items-center gap-1.5">
          <label htmlFor={id} className="text-sm text-slate-700">
            {label}
            {required ? (
              <span className="ml-0.5 text-red-600" aria-hidden="true">
                *
              </span>
            ) : null}
          </label>

          {description ? <HelpTip label={description} /> : null}
        </div>
      ) : null}

      {description ? (
        <span id={`${id}-desc`} className="sr-only">
          {description}
        </span>
      ) : null}

      <div ref={triggerRef} className="relative">
        <div
          className={[
            'flex min-h-10 w-full flex-wrap items-center gap-1.5 rounded-lg border bg-white py-1.5',
            /*
              The right gutter is the overlaid controls' room. `Clear` (~42px) plus the chevron
              (~24px) and their 8px inset is ~76px — more than `pr-16`'s 64px, so a chip wrapped
              under `Clear` (09/12 Oct 2026). The wider gutter only while `Clear` is drawn.
            */
            value.length > 1 && !disabled ? 'pr-24' : 'pr-16',
            remote ? 'pl-10' : 'pl-2',
            shownError
              ? 'border-red-400 focus-within:border-red-500 focus-within:ring-2 focus-within:ring-red-100'
              : 'border-slate-300 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100',
            disabled ? 'cursor-not-allowed bg-slate-50' : '',
            className,
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {remote ? (
            <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400">
              <Database className="h-4 w-4" aria-hidden="true" />
            </span>
          ) : null}

          {chosen.map((option) => (
            <span
              key={option.value}
              className="inline-flex max-w-full items-center gap-1 rounded-md bg-slate-100 py-0.5 pl-2 pr-1 text-xs text-slate-700"
            >
              <span className="min-w-0 truncate">{option.label}</span>
              {!disabled ? (
                <button
                  type="button"
                  id={`${id}-remove-${option.value}`}
                  onClick={() => toggle(option.value)}
                  aria-label={`Remove ${option.label}`}
                  className="rounded p-0.5 text-slate-400 transition hover:bg-slate-200 hover:text-slate-600"
                >
                  <X className="h-3 w-3" aria-hidden="true" />
                </button>
              ) : null}
            </span>
          ))}

          <input
            id={id}
            type="text"
            role="combobox"
            aria-expanded={open}
            aria-controls={`${id}-panel`}
            aria-autocomplete="list"
            aria-label={ariaLabel ?? label}
            aria-invalid={shownError ? true : undefined}
            aria-describedby={describedBy || undefined}
            autoComplete="off"
            disabled={disabled}
            readOnly={!searchable}
            value={query}
            placeholder={chosen.length === 0 ? placeholder : ''}
            onChange={(e) => {
              setQuery(e.target.value);
              if (!open) setOpen(true);
            }}
            onFocus={() => remote && setOpen(true)}
            onBlur={markTouched}
            onClick={() => !disabled && setOpen(true)}
            className="min-w-24 flex-1 border-0 bg-transparent p-0 text-sm text-slate-600 placeholder:text-slate-400 focus:outline-none focus:ring-0"
          />
        </div>

        {/* Comma-joined so a plain form post carries the selection. */}
        <input type="hidden" name={name ?? id} value={value.join(',')} />

        <span className="absolute inset-y-0 right-0 flex items-center gap-0.5 pr-2">
          {value.length > 1 && !disabled ? (
            <button
              type="button"
              id={`${id}-clear`}
              onClick={() => onChange([])}
              className="rounded-md px-1.5 py-1 text-xs text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
            >
              Clear
            </button>
          ) : null}

          <span className="p-1 text-slate-400" aria-hidden="true">
            <ChevronDown className="h-4 w-4" />
          </span>
        </span>
      </div>

      {shownError ? (
        <p id={`${id}-err`} className="text-xs text-red-600" role="alert">
          {shownError}
        </p>
      ) : null}

      {open && rect
        ? createPortal(
            <div
              ref={panelRef}
              id={`${id}-panel`}
              role="listbox"
              aria-multiselectable="true"
              style={{ position: 'fixed', top: rect.top, left: rect.left, width: rect.width }}
              className={`modern-scrollbar ${LAYER.PANEL} max-h-72 overscroll-contain overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-raised`}
            >
              {loading ? (
                <p className="px-3 py-2 text-sm text-slate-400">Loading…</p>
              ) : filtered.length === 0 ? (
                <p className="px-3 py-2 text-sm text-slate-400">
                  {query ? `No options match "${query}"` : 'No options available'}
                </p>
              ) : (
                filtered.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    id={`${id}-option-${option.value}`}
                    role="option"
                    aria-selected={value.includes(option.value)}
                    disabled={option.disabled}
                    onClick={() => toggle(option.value)}
                    className={[
                      'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition hover:bg-slate-50',
                      option.disabled
                        ? 'cursor-not-allowed text-slate-300'
                        : value.includes(option.value)
                          // A selected option is the darkest thing in the list.
                          ? 'font-medium text-slate-900'
                          : 'text-slate-600',
                    ].join(' ')}
                  >
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                      {value.includes(option.value) ? (
                        <Check className="h-4 w-4 text-blue-600" aria-hidden="true" />
                      ) : null}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{option.label}</span>
                      {option.sublabel ? (
                        <span className="block truncate text-xs text-slate-400">{option.sublabel}</span>
                      ) : null}
                    </span>
                    {option.right ? (
                      <span className="shrink-0 text-xs text-slate-400">{option.right}</span>
                    ) : null}
                  </button>
                ))
              )}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
